// Archivo: ui/TransferForm.js

import React, { useState, useEffect, useMemo } from 'react';
import { Combobox } from '@headlessui/react';

// --- CAMBIO: El formulario ahora acepta 'initialData' para la edición ---
export default function TransferForm({ stock, locations, onSave, onCancel, showModal, currentUser, initialData = null }) {
    const [formData, setFormData] = useState({
        originLocationId: '',
        destinationLocationId: '',
        stockItemId: '',
        quantity: 1,
    });
    const [availableStock, setAvailableStock] = useState([]);
    const [selectedStockItem, setSelectedStockItem] = useState(null);
    const [stockItemQuery, setStockItemQuery] = useState('');

    const originLocations = useMemo(() => {
        if (currentUser.role === 'produccion' || currentUser.role === 'bodega') {
            return locations.filter(loc => loc.id === currentUser.location);
        }
        return locations;
    }, [locations, currentUser]);

    // --- CAMBIO: Se añade un useEffect para rellenar el formulario al editar ---
    useEffect(() => {
        if (initialData) {
            // Si estamos editando, llenamos el formulario con los datos existentes
            setFormData({
                originLocationId: initialData.stockItem.locationId,
                destinationLocationId: initialData.destinationLocationId,
                stockItemId: initialData.stockItem.id,
                quantity: initialData.quantity,
            });
            setSelectedStockItem(initialData.stockItem);
        } else if ((currentUser.role === 'produccion' || currentUser.role === 'bodega') && originLocations.length === 1) {
            // Si es un usuario de bodega/producción, se autoselecciona su ubicación
            setFormData(prev => ({ ...prev, originLocationId: originLocations[0].id }));
        }
    }, [initialData, currentUser, originLocations]);


    useEffect(() => {
        if (formData.originLocationId) {
            const filteredStock = stock.filter(item => item.locationId === formData.originLocationId && item.quantity > 0);
            setAvailableStock(filteredStock);
        } else {
            setAvailableStock([]);
        }
        // No reseteamos todo si estamos editando
        if (!initialData) {
            setFormData(prev => ({ ...prev, stockItemId: '' }));
            setSelectedStockItem(null);
        }
    }, [formData.originLocationId, stock, initialData]);
    
    const handleStockItemChange = (stockItemId) => {
        const item = stock.find(s => s.id === stockItemId);
        setSelectedStockItem(item);
        setFormData(prev => ({ ...prev, stockItemId, quantity: 1 })); // Reseteamos la cantidad a 1 al cambiar de producto
    }

    const filteredAvailableStock =
        stockItemQuery === ''
            ? availableStock
            : availableStock.filter((item) => {
                const searchStr = `${item.productName} ${item.designName} ${item.size} ${item.color}`.toLowerCase().replace(/\s+/g, '');
                return searchStr.includes(stockItemQuery.toLowerCase().replace(/\s+/g, ''));
            });

    const handleSubmit = (e) => {
        e.preventDefault();
        const quantity = parseInt(formData.quantity, 10);
        if (!selectedStockItem) {
            showModal("Error", "Debe seleccionar un producto del stock de origen.");
            return;
        }
        if (quantity > selectedStockItem.quantity) {
            showModal("Error de Stock", `No se puede trasladar más de la cantidad disponible (${selectedStockItem.quantity}).`);
            return;
        }
        if (formData.originLocationId === formData.destinationLocationId) {
            showModal("Error", "La ubicación de origen y destino no pueden ser la misma.");
            return;
        }
        
        onSave({
            stockItem: selectedStockItem,
            quantity: quantity,
            destinationLocationId: formData.destinationLocationId,
        });

        // Limpiamos el formulario solo si no estamos editando
        if (!initialData) {
            setFormData(prev => ({
                ...prev,
                stockItemId: '',
                quantity: 1,
            }));
            setSelectedStockItem(null);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Ubicación de Origen</label>
                    <select name="originLocationId" value={formData.originLocationId} onChange={(e) => setFormData({...formData, originLocationId: e.target.value})} required disabled={currentUser.role === 'produccion' || currentUser.role === 'bodega' || !!initialData} className="input-field mt-1 bg-white disabled:bg-gray-200 focus:border-cyan-500 focus:ring-cyan-500">
                        <option value="">-- Seleccione origen --</option>
                        {originLocations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Ubicación de Destino</label>
                    <select name="destinationLocationId" value={formData.destinationLocationId} onChange={(e) => setFormData({...formData, destinationLocationId: e.target.value})} required className="input-field mt-1 bg-white focus:border-cyan-500 focus:ring-cyan-500">
                        <option value="">-- Seleccione destino --</option>
                        {locations.filter(loc => loc.id !== formData.originLocationId).map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                    </select>
                </div>
            </div>
            
            <div>
                <Combobox
                    value={formData.stockItemId}
                    onChange={handleStockItemChange}
                    disabled={!formData.originLocationId || !!initialData} // Deshabilitado al editar
                >
                    <Combobox.Label className="block text-sm font-medium text-gray-700">Producto en Stock</Combobox.Label>
                    <div className="relative mt-1">
                        <Combobox.Input
                            className="input-field mt-1 bg-white disabled:bg-gray-100 w-full"
                            onChange={(event) => setStockItemQuery(event.target.value)}
                            displayValue={(stockItemId) => {
                                if (!stockItemId) return '';
                                const item = stock.find(i => i.id === stockItemId);
                                return item ? `${item.productName} (${item.designName}) - Talla: ${item.size}, Color: ${item.color} (Disp: ${item.quantity})` : '';
                            }}
                            placeholder="-- Busca un producto del origen --"
                            onClick={(event) => event.target.select()}
                        />
                         <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a.75.75 0 01.53.22l3.5 3.5a.75.75 0 01-1.06 1.06L10 4.81 7.03 7.78a.75.75 0 01-1.06-1.06l3.5-3.5A.75.75 0 0110 3zm-3.78 9.28a.75.75 0 011.06 0L10 15.19l2.97-2.91a.75.75 0 111.06 1.06l-3.5 3.5a.75.75 0 01-1.06 0l-3.5-3.5a.75.75 0 010-1.06z" clipRule="evenodd" /></svg>
                         </Combobox.Button>
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {filteredAvailableStock.map((item) => (
                                <Combobox.Option key={item.id} value={item.id} className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${ active ? 'bg-cyan-600 text-white' : 'text-gray-900' }`}>
                                    {({ selected }) => (
                                        <span className={`block ${ selected ? 'font-semibold' : '' }`}>
                                            {item.productName} ({item.designName}) - Talla: {item.size}, Color: {item.color} (Disp: {item.quantity})
                                        </span>
                                    )}
                                </Combobox.Option>
                            ))}
                        </Combobox.Options>
                    </div>
                </Combobox>
            </div>
            
            <div>
                <label className="block text-sm font-medium text-gray-700">Cantidad a Trasladar</label>
                <input type="number" name="quantity" min="1" max={selectedStockItem?.quantity || 1} value={formData.quantity} onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value, 10)})} required disabled={!selectedStockItem} className="input-field mt-1"/>
            </div>
            <div className="flex justify-end pt-4 border-t">
                <button type="button" onClick={onCancel} className="btn-secondary mr-3">Cerrar</button>
                {/* --- CAMBIO: El texto del botón es dinámico --- */}
                <button type="submit" className="btn-primary">
                    {initialData ? 'Actualizar Traslado' : 'Añadir al Lote'}
                </button>
            </div>
        </form>
    );
}